import type { ReactNode } from 'react'
import {
  AlertTriangle,
  ExternalLink,
  Archive,
  ArchiveRestore,
  Send,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import {
  displayNameFor,
  ARCHIVE_BLOCKED_REASON,
  dealCloudEntryUrl,
  reportSendError,
  reportSendOutcome,
  sendIsReady,
  sendNote,
  sentDealCloudEntryId,
  isArchived,
  syncStatusFor,
} from '@/features/deal-review/deal-lead-views'
import {
  useDealLeadCounts,
  useSendToDealCloud,
  useSetDealLeadArchived,
} from '@/hooks/use-deal-leads'
import { showError } from '@/lib/toast'
import type { DealLeadDetail, DealLeadListItem } from '@/types/api'
import { OwnClick } from '@/components/custom/deal-review/inbox-cells'

export function SendCell({
  lead,
  onSent,
}: {
  lead: DealLeadListItem
  onSent?: (detail: DealLeadDetail) => void
}) {
  const counts = useDealLeadCounts()
  const send = useSendToDealCloud(lead.id)
  const writeEnabled = counts.data?.dealCloudWriteEnabled
  const ready = sendIsReady(lead, writeEnabled)
  const blocked = lead.capabilities.sendBlockedReason ?? null
  const note = ready ? sendNote(lead) : null
  const description = blocked ?? note
  const disabled = !ready || send.isPending
  const tooltip =
    description ??
    (send.isPending
      ? 'Sending to DealCloud…'
      : disabled
        ? "Can't send to DealCloud right now"
        : 'Send to DealCloud')
  const reasonId = `send-reason-${lead.id}`
  const baseLabel = `Send ${displayNameFor(lead) ?? 'this lead'} to DealCloud`
  // In aria-label, not just aria-describedby, so a screen reader announces it too.
  const accessibleLabel = note ? `${baseLabel}. ${note}` : baseLabel

  const button = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      disabled={disabled}
      aria-label={accessibleLabel}
      aria-describedby={description === null ? undefined : reasonId}
      className="size-8 text-primary/70 disabled:opacity-40"
      onClick={() =>
        send.mutate(
          { version: lead.version },
          {
            onSuccess: (detail) => {
              reportSendOutcome(detail)
              onSent?.(detail)
            },
            onError: reportSendError,
          }
        )
      }
    >
      <Send aria-hidden="true" className="size-4" />
    </Button>
  )

  return (
    <OwnClick>
      <Tooltip>
        {/* A disabled button suppresses pointer events, so its tooltip hangs on a wrapper. */}
        <TooltipTrigger asChild>
          {disabled ? <span className="inline-flex">{button}</span> : button}
        </TooltipTrigger>
        <TooltipContent className="max-w-64">{tooltip}</TooltipContent>
      </Tooltip>
      {description !== null && (
        <span id={reasonId} className="sr-only">
          {description}
        </span>
      )}
    </OwnClick>
  )
}

export function ArchiveCell({ lead }: { lead: DealLeadListItem }) {
  const archive = useSetDealLeadArchived(lead.id)
  const archived = isArchived(lead)
  const label = archived
    ? `Restore ${displayNameFor(lead) ?? 'this lead'} to the Inbox`
    : `Archive ${displayNameFor(lead) ?? 'this lead'}`

  const button = (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      disabled={!lead.capabilities.canArchive || archive.isPending}
      aria-label={label}
      className="size-8 text-primary/70 disabled:opacity-40"
      onClick={() =>
        archive.mutate(
          { version: lead.version, archived: !archived },
          {
            onError: (error) =>
              showError(
                error,
                archived
                  ? 'That lead could not be restored'
                  : 'That lead could not be archived'
              ),
          }
        )
      }
    >
      {archived ? (
        <ArchiveRestore aria-hidden="true" className="size-4" />
      ) : (
        <Archive aria-hidden="true" className="size-4" />
      )}
    </Button>
  )

  if (lead.capabilities.canArchive) return <OwnClick>{button}</OwnClick>

  return (
    <OwnClick>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{button}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-64">
          {ARCHIVE_BLOCKED_REASON}
        </TooltipContent>
      </Tooltip>
      <span className="sr-only">{ARCHIVE_BLOCKED_REASON}</span>
    </OwnClick>
  )
}

function DealCloudMark({ lead }: { lead: DealLeadListItem }) {
  const entryId = sentDealCloudEntryId(lead)
  if (entryId) {
    const url = dealCloudEntryUrl(entryId)
    const label = `DealCloud ${entryId}`

    // Unconfigured environment: id as plain text, never a link that goes nowhere.
    if (!url) {
      return (
        <span
          aria-label={label}
          className="inline-flex size-8 items-center justify-center text-xs text-primary/60"
        >
          {entryId}
        </span>
      )
    }

    return (
      <OwnClick>
        <Button
          asChild
          variant="ghost"
          size="icon"
          className="size-8 text-primary/70"
        >
          <a href={url} target="_blank" rel="noreferrer" aria-label={label}>
            <ExternalLink aria-hidden="true" className="size-4" />
          </a>
        </Button>
      </OwnClick>
    )
  }

  if (lead.dealCloudSyncState === 'FAILED') {
    const reason = syncStatusFor(lead)?.label ?? 'DealCloud send failed'
    const reasonId = `dealcloud-reason-${lead.id}`

    return (
      <OwnClick>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              aria-describedby={reasonId}
              className="inline-flex size-8 items-center justify-center text-destructive/70"
            >
              <AlertTriangle aria-hidden="true" className="size-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent>{reason}</TooltipContent>
        </Tooltip>
        <span id={reasonId} className="sr-only">
          {reason}
        </span>
      </OwnClick>
    )
  }

  return null
}

export function HistoryActionsCell({
  lead,
  onSent,
}: {
  lead: DealLeadListItem
  onSent?: (detail: DealLeadDetail) => void
}) {
  return (
    <div className="flex items-center gap-0.5">
      {lead.dealCloudSyncState === 'SENT' ? null : (
        <SendCell lead={lead} onSent={onSent} />
      )}
      <DealCloudMark lead={lead} />
      <ArchiveCell lead={lead} />
    </div>
  )
}

export function InboxActionsCell({
  lead,
  onSent,
}: {
  lead: DealLeadListItem
  onSent?: (detail: DealLeadDetail) => void
}) {
  // The failure itself shows as the Decision cell's badge, so only the sent link joins here.
  const sent = lead.dealCloudSyncState === 'SENT'
  return (
    <>
      {sent ? (
        <DealCloudMark lead={lead} />
      ) : (
        <SendCell lead={lead} onSent={onSent} />
      )}
      <ArchiveCell lead={lead} />
    </>
  )
}

export function RowActionRail({ children }: { children: ReactNode }) {
  return <div className="-ml-1.5 flex items-center gap-0.5">{children}</div>
}
